import { SensorConfigurationBase, VoltageRequirement } from "./SensorConfigurationTypes.js";

// Provide either kFactor or litersPerPulse, not both
type KFactorOptions =
    | {
          kFactor: number;
          litersPerPulse?: never;
      }
    | {
          kFactor?: never;
          litersPerPulse: number;
      };

type MinRawValueOptions =
    // Provide minRawValue in Hz or liters, not both
    | {
          minRawValueHz: number;
          minRawValueLitersPerMinute?: never;
      }
    | {
          minRawValueHz?: never;
          minRawValueLitersPerMinute: number;
      };

type MaxRawValueOptions =
    // Provide maxRawValue in Hz or liters, not both
    | {
          maxRawValueHz?: number;
          maxRawValueLitersPerMinute?: never;
      }
    | {
          maxRawValueHz?: never;
          maxRawValueLitersPerMinute?: number;
      };

/**
 * Use this to generate a flowmeter sensor config without having to specify everything manually. Just put in
 * the coefficients and this should support any linear PWM flowmeter.
 *
 * You will still need to specify "manufacturerName", "modelName", "friendlyName", and "friendlyShortName" since
 * those cannot be generated automatically
 *
 * Ensure input units are correct:
 *
 * Flowmeter K-factor specifications typically use Gallons unless otherwise specified. We need to convert to Liters.
 * 1 gal (US) = 3.785411784 L
 * Alternatively, if a Hydrawise K-factor (L/pulse) is provided, we can use that directly with the litersPerPulse parameter.
 *
 * min/max flow rate values are typically specified in volume per minute (L/min or Gal/min), but we can also accept them in Hz.
 * This function will convert values in L/min to Hz for calculations.
 *
 * @param minRawValueHz the minimum raw (PWM) value, below which the parsed value should be 0. Units: Hz
 * @param minRawValueLiters the minimum raw (PWM) value, below which the parsed value should be 0. Units: L/min
 * @param maxRawValueHz the maximum raw (PWM) value, above which the parsed value should be whatever the maxRawValue converts to. Units: Hz
 * @param maxRawValueLiters the maximum raw (PWM) value, above which the parsed value should be whatever the maxRawValue converts to. Units: L/min
 * @param kFactor coefficient in the conversion formula (freq + offset ) * kFactor. Units: (1/60 L/pulse) (typically written in gallons in flowmeter specifications)
 * @param litersPerPulse K-factor with different units, sometimes called Hydrawise K-factor. Easier to verify if included directly in sensor specification. Converted to K-Factor for the calculations. Units: L/pulse
 * @param offset the constant term in the conversion formula (freq + offset ) * kFactor
 * @param skipInverseFormula if true, the inverse formula will not be generated.
 * @param precisionImportance can be set to high (16 bit), medium (12 bit), or low (8 bit)
 * @param supportsLowPowerTotalization
 */
type FlowmeterConfigParams = {
    maxRawValueHz?: number;
    maxRawValueLiters?: number;
    offset: number;
    skipInverseFormula?: boolean;
    precisionImportance?: "high" | "medium" | "low";
    supportsLowPowerTotalization?: boolean;
} & KFactorOptions &
    MinRawValueOptions &
    MaxRawValueOptions;

export function generateFlowmeterConfiguration({
    minRawValueHz,
    minRawValueLitersPerMinute,
    maxRawValueHz,
    maxRawValueLitersPerMinute,
    kFactor,
    offset,
    litersPerPulse,
    skipInverseFormula = false,
    precisionImportance = "medium",
    supportsLowPowerTotalization = false,
}: FlowmeterConfigParams): Omit<
    SensorConfigurationBase,
    "manufacturerName" | "modelName" | "friendlyName" | "friendlyShortName" | "key"
> & {
    sensorParameterConfigs: Record<string, never>;
    parsedDataType: "v1flowmeter";
    minimumReading: 0;
    useZeroOrderHoldInterpolation: true;
    unitCategory: "liquidFlow";
    isOnboard: false;
    communicationProtocol: "PWM";
    openValveIncreasesDatabaseValue: true;
} {
    // kFactor (1/60 L/pulse) = litersPerPulse (L/pulse) * 60
    kFactor = kFactor ?? litersPerPulse! * 60;

    // pulse_frequency (pulse/second or Hz) = flow_rate (L/min) / K (L seconds/pulse minutes) - offset (pulse/second or Hz)
    minRawValueHz = minRawValueHz ?? minRawValueLitersPerMinute! / kFactor - offset;
    maxRawValueHz = maxRawValueHz ?? maxRawValueLitersPerMinute! / kFactor - offset;

    // The equation being assumed here is freq = flow_rate / k - offset
    const basicFormula = (freq: number): number => (freq + offset) * kFactor;
    const getParsedValueFromRawValue = (freq: number) => {
        if (freq < minRawValueHz || freq == 0) {
            return 0;

            // If frequency is too high, consider data as the highest specification
        } else if (maxRawValueHz !== undefined && freq > maxRawValueHz) {
            return basicFormula(maxRawValueHz);
        }

        // Regular conversion if data is in range
        return basicFormula(freq);
    };

    return {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "2:4.*.*.*.*",
            rawDataFormat: "PWM",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (freq, options) => [
                {
                    dataType: options.dataType,
                    value: getParsedValueFromRawValue(freq),
                },
            ],

            parsedDataToRawDataConversion: skipInverseFormula
                ? undefined
                : (value) => {
                      // If value is too low, the output should be 0
                      if (value < basicFormula(minRawValueHz)) {
                          return 0;

                          // If value is too high, consider the output to be the highest frequency
                      } else if (maxRawValueHz !== undefined && value > basicFormula(maxRawValueHz)) {
                          return maxRawValueHz;
                      }

                      // Regular conversion if data is in range
                      return value / kFactor - offset;
                  },
            precisionImportance: precisionImportance,
            rawAndParsedValuesIncreaseTogether: kFactor > 0,
            supportsLowPowerTotalization: supportsLowPowerTotalization,
        },
        sensorParameterConfigs: {},
        parsedDataType: "v1flowmeter",
        measurementUncertainty: kFactor * 2,
        minimumReading: 0,
        useZeroOrderHoldInterpolation: true,
        unitCategory: "liquidFlow",
        isOnboard: false,
        communicationProtocol: "PWM",
        openValveIncreasesDatabaseValue: true,
        saplingConversion: getParsedValueFromRawValue,
    };
}

/**
 * Use this to generate a pressure transducer sensor config without having to specify everything manually.
 * This supports linear ADC-based pressure transducers that output voltage proportional to pressure.
 *
 * You will still need to specify "manufacturerName", "modelName", "friendlyName", and "friendlyShortName" since
 * those cannot be generated automatically.
 *
 * The conversion formula is: pressure = (voltageMultiplier * volt - voltageOffset) * pressureConversionFactor
 * Where pressureConversionFactor converts to PSI (6.89476 converts PSI to kPa)
 *
 * @param voltageMultiplier coefficient multiplied by the voltage reading
 * @param voltageOffset constant subtracted after voltage multiplication
 * @param pressureConversionFactor conversion factor to final pressure units (e.g., 6.89476 converts PSI to kPa)
 * @param precisionImportance can be set to high (16 bit), medium (12 bit), or low (8 bit)
 * @param requiredVoltage the voltage requirement for the sensor (default: VOLTS_5)
 */
type PressureTransducerConfigParams = {
    voltageMultiplier: number;
    voltageOffset: number;
    pressureConversionFactor: number;
    precisionImportance?: "high" | "medium" | "low";
    requiredVoltage?: VoltageRequirement;
};

export function generatePressureTransducerConfiguration({
    voltageMultiplier,
    voltageOffset,
    pressureConversionFactor,
    precisionImportance = "medium",
    requiredVoltage = VoltageRequirement.VOLTS_5,
}: PressureTransducerConfigParams): Omit<
    SensorConfigurationBase,
    "manufacturerName" | "modelName" | "friendlyName" | "friendlyShortName" | "key"
> & {
    sensorParameterConfigs: Record<string, never>;
    parsedDataType: "v1transducer";
    useZeroOrderHoldInterpolation: true;
    unitCategory: "liquidPressure";
    isOnboard: false;
    communicationProtocol: "ADC";
    openValveIncreasesDatabaseValue: true;
} {
    return {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: requiredVoltage,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                return (v / pressureConversionFactor + voltageOffset) / voltageMultiplier;
            },
            rawDataToParsedDataConversion: (volt, options) => [
                {
                    dataType: options.dataType,
                    value: Math.max(0, (voltageMultiplier * volt - voltageOffset) * pressureConversionFactor),
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: precisionImportance,
            rawAndParsedValuesIncreaseTogether: true,
        },
        sensorParameterConfigs: {},
        parsedDataType: "v1transducer",
        useZeroOrderHoldInterpolation: true,
        unitCategory: "liquidPressure",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: true,
    };
}
